import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { EASE, Reveal, scrollToId } from "./Weave";

const MENTORS = [
  {
    name: "James Cracknell",
    title: "MSc System Practitioner · Chief Reinvention Officer",
    img: "/mentors/james.jpg",
    pos: "50% 50%",
    bio: "Experienced in financial management across the for-profit and third sectors, James is an Entrepreneur-in-Residence at the University of Essex. He has personally been through many career changes — and brings that expertise to the design of the Academy. A systems thinker, facilitator and natural networker.",
  },
  {
    name: "Sandrine Singleton-Perrin",
    title: "Director of Community Wellbeing",
    img: "/mentors/sandrine.jpg",
    pos: "50% 50%",
    bio: "A former educationalist and now a qualified Sophrologist, Sandrine leads a team of practitioners and coaches focused on stress, sleep and anxiety — a founder-first approach to wellbeing that underpins everything The Weave does.",
  },
  {
    name: "Adam Roxby",
    title: "Technology & Communications",
    img: "/mentors/adam.jpg",
    pos: "50% 50%",
    bio: "A self-taught technology and communications expert with over 15 years in content creation, Adam was a fully qualified nurse and now develops his counselling programme alongside community work — including the outstanding Interwoven Podcast.",
  },
];

export default function Mentors() {
  return (
    <section id="cohort" data-testid="cohort-section" className="scroll-mt-24">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8 lg:py-32">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <Reveal>
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-wred">Meet the room</p>
            <h2 className="max-w-2xl text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl">
              You don&rsquo;t have to build alone. Here&rsquo;s who&rsquo;s in the room.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-sm text-base leading-relaxed text-body2 lg:pb-2 lg:text-right">
              The Academy is deliberately small and deliberately human. For April 2027, the core
              team is the three of us — with the wider mentor roster announced soon.
            </p>
          </Reveal>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8" data-testid="cohort-grid">
          {MENTORS.map((m, i) => (
            <motion.div
              key={m.name}
              data-testid={`cohort-card-${i}`}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, delay: i * 0.09, ease: EASE }}
              className={`group flex h-full flex-col ${i % 2 === 0 ? "lg:rotate-1" : "lg:-rotate-1"} transition-transform duration-300 hover:rotate-0`}
            >
              <div className="relative overflow-hidden rounded-3xl ring-1 ring-paperborder">
                <img
                  src={m.img}
                  alt={m.name}
                  loading="lazy"
                  style={{ objectPosition: m.pos || "50% 50%" }}
                  className="aspect-[3/4] w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                />
              </div>
              <h3 className="mt-5 text-lg font-bold text-ink">{m.name}</h3>
              <p className="mt-0.5 text-xs font-semibold uppercase tracking-[0.14em] text-tealdark">
                {m.title}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-body2">{m.bio}</p>
            </motion.div>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14 flex flex-col items-start justify-between gap-6 rounded-3xl border border-paperborder bg-white p-8 sm:flex-row sm:items-center lg:p-10">
            <p className="max-w-2xl text-base leading-relaxed text-body2 sm:text-lg">
              More mentors for April 2027 are being announced. Join the waitlist and you&rsquo;ll
              meet them first — names, faces and all.
            </p>
            <button
              data-testid="cohort-cta-join-waitlist"
              onClick={() => scrollToId("waitlist")}
              className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-base font-semibold text-paper transition-colors hover:bg-wred"
            >
              Join the waitlist
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
